import useUrlQuery from '@hooks/useUrlQuery';
import HeaderPageBlock from '@ui/Blocks/Shared/HeaderPageBlock';
import {
  ITBox,
  QboButton,
  QboCard,
  QboCardListItem,
  QboFormGroup,
  QboPagination,
  QboSimpleDataTable,
  QboTextField,
  QboTypography,
} from '@ui/Components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvoiceSeatAdjustmentListMutation } from '@services/WebApiService/InvoiceSeatAdjustment';
import { constant } from '@config/BaseSetting';
import useLocalStorage from '@hooks/useLocalStorage';
import NoStatusSection from '@ui/Sections/SystemStatusPage/NoStatusSection';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PartnerFilterSection from '@ui/Sections/Shared/PartnerFilterSection';
import {
  buttonStyle,
  INVOICE_SEAT_COLUMNS,
} from './pageHooks/constants/invoiceSeatAdjustmentConstants';
import FormModal from './formModal';

const defaultPagination = { pageNumber: 1, pageSize: 10 };

const defaultFilters = {
  resellerId: '',
  period: '',
};

export default function InvoiceSeatAdjustmentPage() {
  const { t } = useTranslation();
  const { query, setQuery } = useUrlQuery();
  const [shownModal, setShownModal] = useState();
  const [accessTokenSet] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);

  // mutation
  const [getSeatAdjustments, getSeatAdjustmentState] = useInvoiceSeatAdjustmentListMutation();
  const { isLoading, rows, errorMessage, statusCode, responsePagination } = useMemo(
    () => ({
      isLoading: getSeatAdjustmentState.isLoading,
      rows:
        getSeatAdjustmentState.data?.data.map((row) => ({
          ...row,
          ...JSON.parse(row.seatPayload),
        })) ?? [],
      errorMessage: getSeatAdjustmentState.error?.data?.message,
      statusCode: getSeatAdjustmentState.error?.status,
      responsePagination: JSON.parse(getSeatAdjustmentState.data?.pagination || '{}'),
    }),
    [getSeatAdjustmentState]
  );
  const [fetchFlag, setFetchFlag] = useState(true);
  const columns = useMemo(() => {
    if (rows.length === 0) return INVOICE_SEAT_COLUMNS;

    const payload = JSON.parse(rows[0].seatPayload);
    const newColumns = Object.keys(payload).map((key) => ({
      label: key,
      columnName: key,
      size: 'sm',
    }));
    return [...INVOICE_SEAT_COLUMNS, ...newColumns];
  }, [rows]);

  // pagination
  const [paginationState, setPaginationState] = useState(defaultPagination);
  const resetPagination = () => setPaginationState(defaultPagination);
  const handleChangePage = (newPage) => {
    setPaginationState({ pageNumber: newPage, pageSize: paginationState.pageSize });
  };
  const handleChangePageSize = (newPageSize) => {
    setPaginationState({ pageNumber: 1, pageSize: newPageSize });
  };
  useEffect(() => {
    setFetchFlag(true);
  }, [paginationState]);

  // filters
  const queryFilters = useMemo(
    () => ({
      period: query.period || '',
      resellerId: query.resellerId || '',
    }),
    [query]
  );
  const [filterState, setFilterState] = useState(queryFilters);
  const handleFilterChange = useCallback(
    (key, value) => {
      setFilterState({ ...filterState, [key]: value });
    },
    [filterState]
  );
  const resetFilters = useCallback(() => {
    setQuery({
      ...query,
      ...defaultFilters,
    });
    setFilterState(defaultFilters);
    resetPagination();
    setFetchFlag(true);
  }, [query]);
  const applyFiltersToQuery = useCallback(() => {
    setQuery({
      ...query,
      ...filterState,
    });
  }, [query, filterState]);

  // fetch handler
  const fetch = useCallback(() => {
    const [year, month] = filterState.period.split('-');

    getSeatAdjustments({
      accessToken: accessTokenSet,
      resellerId: filterState.resellerId,
      pageNumber: paginationState.pageNumber,
      pageSize: paginationState.pageSize,
      filters: {
        year,
        month,
      },
    });
  }, [accessTokenSet, paginationState, filterState]);
  useEffect(() => {
    if (!fetchFlag) return;
    fetch();
    setFetchFlag(false);
  }, [fetchFlag, fetch]);

  // handlers
  const handleOnSearchClick = useCallback(
    (e) => {
      e.preventDefault();
      applyFiltersToQuery();
      resetPagination();
      setFetchFlag(true);
    },
    [applyFiltersToQuery, resetPagination]
  );

  return (
    <>
      <HeaderPageBlock>Invoice Seat Adjustment</HeaderPageBlock>
      <QboCard isMainWrapper>
        <QboFormGroup
          onSubmit={(e) => handleOnSearchClick(e)}
          buttonPostion="start"
          buttonStyle={buttonStyle}
          handleOnResetButton={resetFilters}
          firstButtonLabel={t('partner_details_page.button.search')}
          withGenerateAndResetButton
          buttonLabel="Validate Transfer">
          <div style={{ marginTop: '1rem' }}>
            <PartnerFilterSection
              value={filterState.resellerId}
              handleChange={(resellerId) => handleFilterChange('resellerId', resellerId)}
              label="Partner:"
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Period"
                views={['year', 'month']}
                value={filterState.date}
                maxDate={dayjs()}
                minDate={dayjs().subtract(5, 'year')}
                onChange={(period) => {
                  handleFilterChange('period', `${period.$y}-${period.$M + 1}`);
                }}
                sx={{ marginBottom: 2 }}
              />
            </LocalizationProvider>
          </div>
        </QboFormGroup>
      </QboCard>

      <QboCard noPadding isMainWrapper>
        <ITBox
          direction="row"
          sx={{
            margin: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <QboTypography
            sx={{
              marginLeft: 2,
              marginBottom: 3,
              fontSize: 18,
              color: '#000000DE',
              fontWeight: 600,
            }}
          />
          <QboButton
            style={{ width: 220 }}
            variant="contained"
            onClick={() => {
              setShownModal(true);
            }}
            loading={false}>
            Seat Adjustment
          </QboButton>
        </ITBox>
        {(rows.length > 0 || isLoading) && (
          <QboSimpleDataTable
            sx={{ width: '100%' }}
            rows={rows}
            header={columns}
            loading={isLoading}
          />
        )}
        {(rows.length > 0 || isLoading) && (
          <QboCardListItem
            justifyContent="right"
            alignItems="center"
            style={{ paddingRight: '1.5em' }}
            rightContent={
              <QboPagination
                hasNextLink={responsePagination?.HasNext}
                hasPrevLink={responsePagination?.HasPrevious}
                onClickPrevLink={() => {
                  const newPageNumber = paginationState.pageNumber - 1;
                  handleChangePage(newPageNumber);
                }}
                onClickNextLink={() => {
                  const newPageNumber = paginationState.pageNumber + 1;
                  handleChangePage(newPageNumber);
                }}
                onChangePageNumber={handleChangePageSize}
                pageNumber={paginationState.pageSize}
                paginationLabel="Page size"
              />
            }
          />
        )}

        {!rows?.length &&
          !isLoading &&
          (statusCode === 500 ? (
            <NoStatusSection
              forbiddenErrorMessage={{ heading: t('error'), caption: errorMessage }}
            />
          ) : (
            <NoStatusSection forbiddenErrorMessage={errorMessage} />
          ))}
      </QboCard>

      <FormModal
        title="New Seat Adjustment"
        showModal={shownModal}
        closeModal={() => {
          resetFilters();
          setShownModal(null);
        }}
      />
    </>
  );
}
